import React, { useState, useEffect, useCallback } from 'react';
import TemplateText from 'src/components/TemplateText/TemplateText';
import { DefaultCustomField } from 'src/constants/types';
import { convertHtmlToText } from 'src/helpers/helpers';
import { RichTextFieldFrame } from './RichTextFieldFrame';

// Wrapper component for TemplateText to make it work like a form field
type TemplateTextFieldWrapperProps = {
  value: string;
  onChange: (value: string) => void;
  label: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  errorText?: string;
  multiline?: boolean;
  minHeight?: string;
  fullWidth?: boolean;
  id?: string;
  customFields: DefaultCustomField[];
};

export const TemplateTextFieldWrapper: React.FC<TemplateTextFieldWrapperProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  errorText,
  customFields,
}) => {
  // Convert plain text with {{}} to HTML format expected by TemplateText
  const convertTextToHtml = useCallback((text: string): string => {
    if (!text) return '';

    // Replace {{variableName}} with HTML mention spans
    const converted = text.replace(/\{\{([^}]+)\}\}/g, (match, fieldName) => {
      const field = customFields.find(f => f.name === fieldName);
      if (field) {
        return `<span data-type="customFieldMention" class="mention" data-id="${fieldName}" data-label="${field.description}" contenteditable="false">${field.description}</span>`;
      }
      return match;
    });

    // Convert newlines to <br/>
    return converted.replace(/\n/g, '<br/>');
  }, [customFields]);

  const [htmlValue, setHtmlValue] = useState(convertTextToHtml(value));

  useEffect(() => {
    setHtmlValue(convertTextToHtml(value));
  }, [value, convertTextToHtml]);

  const handleUpdate = (newHtml: string) => {
    const plainText = convertHtmlToText(newHtml);
    onChange(plainText);
  };

  return (
    <RichTextFieldFrame
      label={label}
      error={error}
      disabled={disabled}
      helperText={errorText || helperText}
      helperTextIsError={!!errorText}
    >
      <TemplateText
        text={htmlValue}
        updateText={handleUpdate}
        customFields={customFields}
        messageTemplates={[]}
        disabled={disabled}
        showConfirmationText={false}
        confirmationText=""
        setFocus={false}
        minHeight="auto"
        padding="16.5px 14px"
        hideMessageTemplates={true}
        hideToolButtons={true}
        hideAbsentDefaultValueAlert={true}
      />
    </RichTextFieldFrame>
  );
};
